import { showLoadingToast } from "vant";
import { useChatSession } from "@/stores/chatSession";
import i18n from "@/locales";
import { Mate } from "@/types/entity";
import { useGlobalUser } from "@/stores/globalUser";
import { useUI } from "@/stores/ui";
import { useMate } from "@/stores/mate";
import { ModelEnum } from "@/enums/chatEnums";

export async function startChatSessionByPopup(
  mate: Mate,
  forceCreate = false,
  sceneId?: number
) {
  const user = useGlobalUser();
  const mateStore = useMate();
  const uiStore = useUI();
  if (mateStore?.currentMate?.uuid === mate.uuid) {
    uiStore.showAnotherMateSelector = true;
    return;
  }
  if (mate.isNsfw && (!user.user?.isAdult || !user.user?.isNsfwVisible)) {
    user.nsfwAgeConfirm.show = true;
    user.nsfwAgeConfirm.callback = () => {
      startChatSessionByPopup(mate);
    };
    return;
  }
  await startChatSession(mate.id, mate.uuid, forceCreate, sceneId);
}

export async function startChatSession(
  mateId: number,
  mateUuid: string,
  forceCreate = false,
  sceneId?: number
) {
  const toast = showLoadingToast({
    message: i18n.global.t("Message.snacksDuringChatPreparation"),
  });
  try {
    const chatSession = useChatSession();
    const mateStore = useMate();
    const userStore = useGlobalUser();
    const isSessionExist = userStore.isLogin
      ? await chatSession.isSessionExist(mateId)
      : false;
    if (!forceCreate && isSessionExist) {
      await goChatMateList(mateUuid);
    } else {
      const sessionStore = useChatSession();
      sessionStore.chatPrepare.mate = (await mateStore.getOrFetchMate(
        mateUuid
      )) as Mate;
      sessionStore.chatPrepare.sceneId = sceneId ?? undefined;
      sessionStore.chatPrepare.showRoleSetting = true;
    }
  } finally {
    toast.close();
  }
}

export const createPrivateSession = async (
  mateId: number,
  mateUuid: string,
  routerEnterMode: "push" | "replace" | "none" = "push",
  sceneId?: number,
  personaId?: number,
  myMateId?: number
) => {
  const loading = showLoadingToast({
    message: i18n.global.t("Message.settingChatEnvironment"),
  });
  try {
    const chatSessionStore = useChatSession();
    const userStore = useGlobalUser();
    const session = await chatSessionStore.createPrivateSession(
      mateId,
      sceneId,
      personaId,
      myMateId
    );
    if (!userStore.isHideNSFW) {
      await chatSessionStore.updateSession(session.id!, {
        model: ModelEnum.STELLA,
        isNsfw: 1,
        matePersonaId: session.matePersonaId,
      });
    }
    await chatSessionStore.goChat(
      window.$router,
      mateUuid,
      session.id!,
      routerEnterMode === "replace"
    );
  } finally {
    loading.close();
  }
};

/**
 * 用于匹配描述性文字
 * 例如：*妙啊，*妙啊*，(妙啊，(妙啊)，（妙啊，（妙啊）
 */
export const DEPICT_MATCH_REG = new RegExp(
  /(\*[^*]*\*?)|(\([^)]*\)?)|(（[^）]*）?)/,
  "g"
);
export const SYMBOL_MATCH_REG = new RegExp(/[*()（）]/, "g");

export function getMatchedIndex(text: string, reg: RegExp) {
  const result: { start: number; end: number }[] = [];
  let match;
  while ((match = reg.exec(text)) !== null) {
    result.push({ start: match.index, end: match.index + match[0].length });
  }
  return result;
}

/**
 * 为描述性文字添加样式
 * 输入:
 *  chunks: ["真是*妙啊*", "妙（妙啊）啊*妙啊*]
 *  reg: {@link DEPICT_MATCH_REG}
 * 输出：["真是<span class="text-base-content/50">*妙啊*</span>", "妙<span class="text-base-content/50">（妙啊）</span>啊<span class="text-base-content/50">*妙啊*</span>"]
 * @param chunks 文字块
 * @param reg 匹配规则
 */
export function styleDepictChunks(chunks: string[], reg: RegExp) {
  const matchedPosList = getMatchedIndex(chunks.join(""), reg);
  if (!chunks || !matchedPosList) return chunks;
  let currentMatchedIdx = 0;
  let textLength = 0;
  const chunksWithStyle: string[] = [];
  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    const chunkStartIdx = textLength;
    const chunkEndIdx = textLength + chunk.length;
    const stylePos = [];
    while (currentMatchedIdx < matchedPosList.length) {
      // 1. 当前chunk的结束位置在匹配的位置之前 => 处理下一个chunk
      if (matchedPosList[currentMatchedIdx].start > chunkEndIdx) {
        break;
      }
      // 2. 当前chunk的开始位置在匹配的位置之后 => 处理下一个匹配区间
      if (matchedPosList[currentMatchedIdx].end < chunkStartIdx) {
        currentMatchedIdx++;
        break;
      }
      // 3. 当前chunk的开始位置在匹配区间上 => 添加样式
      const startPos = matchedPosList[currentMatchedIdx].start - chunkStartIdx;
      const endPos =
        Math.min(matchedPosList[currentMatchedIdx].end, chunkEndIdx) -
        chunkStartIdx;
      stylePos.push([startPos, endPos]);

      if (matchedPosList[currentMatchedIdx].end <= chunkEndIdx) {
        // 3.1 如果当前chunk的结束位置在匹配的位置之前 => 处理下一个匹配区间
        currentMatchedIdx++;
      } else {
        // 3.2 如果当前chunk的结束位置在匹配的位置之后 => 处理下一个chunk
        break;
      }
    }
    // 4. 在chunk中添加样式
    if (stylePos.length > 0) {
      // 4.1 如果有匹配的区间 => 添加样式
      let styledChunk = "";
      let lastIndex = 0;
      stylePos.forEach(([start, end]) => {
        const untagged = chunk.substring(lastIndex, start);
        const tagged =
          `<span class="text-base-content/50 italic">` +
          chunk.substring(start, end) +
          `</span>`;
        styledChunk += untagged + tagged;
        lastIndex = end;
      });
      if (lastIndex < chunk.length) {
        styledChunk += chunk.substring(lastIndex);
      }
      chunksWithStyle[i] = styledChunk;
    } else {
      // 4.2 如果没有匹配的区间 => 不添加样式
      chunksWithStyle[i] = chunk;
    }
    textLength += chunk.length;
  }
  return chunksWithStyle;
}

export const goChatMateList = async (uuid: string, replace?: boolean) => {
  if (replace) {
    await window.$router.replace({ name: "mateChatList", params: { uuid } });
  } else {
    await window.$router.push({ name: "mateChatList", params: { uuid } });
  }
};
